"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { ArrowRightIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { CDN_ASSETS } from "@/lib/cdn";
import { AppDispatch } from "@/store/store";
import { OtpRequestError, registerInitiate, verifyRegisterOtp } from "@/store/slices/authSlice";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[A-Za-z]+$/;
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

// Sentinel formik.errors.email value for any registerInitiate server-side
// rejection (blocked/disposable domain, already-registered email, etc.) — the
// UI shows one fixed message for all of them, instead of
// relaying the backend's per-case wording.
const SERVER_EMAIL_ERROR = "SERVER_EMAIL_ERROR";
const SUPPORT_EMAIL = "support@infynd.co.in";

// The soft field shell. These override the shadcn defaults through the `cn()`
// call inside each component, so the last conflicting class wins.
//
// No focus-visible or aria-invalid border/ring recolor — the Input strips
// all of that and keeps the border the
// same color always. `--brand` itself is a red-orange hue, so the old
// focus-visible:border-brand read as the same "red highlight" the invalid
// state used, just on click instead of on error.
const FIELD_SHELL =
  "h-11 rounded-xl border-border bg-muted/45 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:border-border aria-invalid:border-border aria-invalid:ring-0 dark:aria-invalid:ring-0";

export default function RegisterPage() {
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = React.useState<"details" | "otp">("details");
  const [loading, setLoading] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const [cooldown, setCooldown] = React.useState(0);
  // The email the server last answered account_exists for. Kept out of
  // formik.errors, which the email field's blur re-validates away — blurring
  // it to click the Sign in link would otherwise remove the link mid-click.
  // Editing the email hides it.
  const [existingEmail, setExistingEmail] = React.useState("");
  const otpRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
    },
    validate: (values) => {
      const errors: { firstName?: string; lastName?: string; companyName?: string; email?: string } = {};
      if (!values.firstName.trim()) errors.firstName = "First name is required";
      else if (!NAME_REGEX.test(values.firstName)) errors.firstName = "Only letters are allowed";
      else if (values.firstName.trim().length < 2) errors.firstName = "First name too short";
      else if (values.firstName.trim().length > 50) errors.firstName = "First name too long";
      if (!values.lastName.trim()) errors.lastName = "Last name is required";
      else if (!NAME_REGEX.test(values.lastName)) errors.lastName = "Only letters are allowed";
      else if (values.lastName.trim().length < 2) errors.lastName = "Last name too short";
      else if (values.lastName.trim().length > 50) errors.lastName = "Last name too long";
      if (!values.companyName.trim()) errors.companyName = "Company name is required";
      if (!values.email.trim()) errors.email = "Email is required";
      else if (!EMAIL_REGEX.test(values.email)) errors.email = "Enter a valid email address";
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      setExistingEmail("");
      try {
        await dispatch(registerInitiate(values)).unwrap();
        toast.success("A verification code has been sent to your email");
        setOtp("");
        setStep("otp");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (error) {
        const { message, code } = (error ?? {}) as OtpRequestError;
        if (code === "account_exists") {
          // Same as fnx: an already-registered email is pointed to sign in.
          setExistingEmail(values.email);
        } else if (code === "otp_cooldown") {
          toast.error(message);
        } else {
          // The remaining server-side failures (blocked/disposable domain
          // etc.) are about the email value — surface them in the email
          // field's own error slot instead of a toast, so they read the same
          // as the client-side "Enter a valid email address" message rather
          // than a separate notification style. One fixed message covers
          // every case here.
          formik.setFieldTouched("email", true, false);
          formik.setFieldError("email", SERVER_EMAIL_ERROR);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  async function submitOtp(event: React.FormEvent) {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) return;

    setLoading(true);
    try {
      await dispatch(verifyRegisterOtp({ email: formik.values.email, otp })).unwrap();
      toast.success("Account verified — welcome to Infynd");
      // A document request, not router.replace() — same reason as the login
      // page: the session cookie only exists as of the line above, and the App
      // Router has /home's logged-out redirect cached from a prefetch made
      // before it. See app/auth/login/page.tsx for the full explanation.
      //
      // replace(), not assign() — otherwise the browser's back button returns
      // to this now-stale sign-up screen instead of leaving the authenticated app.
      window.location.replace("/home");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  }

  async function resendOtp() {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await dispatch(
        registerInitiate({
          email: formik.values.email,
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          companyName: formik.values.companyName,
        })
      ).unwrap();
      toast.success("A new verification code has been sent");
      setOtp("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      toast.error((error as OtpRequestError | undefined)?.message || "Unable to resend code");
    } finally {
      setLoading(false);
    }
  }

  function handleOtpChange(value: string, index: number) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = otp.split("");
    next[index] = digit;
    const joined = next.join("").slice(0, OTP_LENGTH);
    setOtp(joined);
    if (digit && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus();
  }

  function handleOtpKeyDown(event: React.KeyboardEvent<HTMLInputElement>, index: number) {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  // Each box has maxLength=1, so a browser paste gets truncated to one
  // character before onChange ever sees it — this has to intercept the
  // clipboard event directly to spread the full code across the boxes.
  function handleOtpPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    setOtp(pasted);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  function handleNameChange(field: "firstName" | "lastName", value: string) {
    formik.setFieldValue(field, value.replace(/[^A-Za-z]/g, "").slice(0, 50));
  }

  const fieldError = (field: keyof typeof formik.values) =>
    formik.touched[field] ? formik.errors[field] : undefined;

  const showExisting =
    !!existingEmail && existingEmail === formik.values.email && !fieldError("email");

  const isDetailsComplete =
    !!formik.values.firstName.trim() &&
    !!formik.values.lastName.trim() &&
    !!formik.values.companyName.trim() &&
    !!formik.values.email.trim();

  return (
    <div className="w-full max-w-sm bg-white">
      <div className="flex flex-col items-center text-center">
        <Image
          src={CDN_ASSETS.logo}
          alt="Infynd"
          width={202}
          height={93}
          priority
          className="h-9 w-auto sm:h-10"
        />

        {step === "details" ? (
          <div>
            <h2 className="text-[28px] leading-tight font-medium tracking-tight sm:text-[34px]">
              Create your account
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Start on the Free Forever plan — no credit card required.
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-[28px] leading-tight font-medium tracking-tight sm:text-[34px]">
              Verify your email
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              We sent a {OTP_LENGTH}-digit code to{" "}
              <span className="font-medium text-foreground">{formik.values.email}</span>.
            </p>
          </div>
        )}
      </div>

      {step === "details" ? (
        <>
          <form onSubmit={formik.handleSubmit} noValidate>
            <FieldGroup className="mt-6 gap-4">
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-3">
                <Field data-invalid={!!fieldError("firstName") || undefined}>
                  <FieldLabel htmlFor="firstName" className="text-[13px] font-medium">
                    First name <span className="text-brand">*</span>
                  </FieldLabel>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="Jane"
                    value={formik.values.firstName}
                    onChange={(e) => handleNameChange("firstName", e.target.value)}
                    onBlur={(e) => {
                      formik.setFieldTouched("firstName", true, false);
                      handleNameChange("firstName", e.target.value);
                    }}
                    aria-invalid={!!fieldError("firstName")}
                    className={FIELD_SHELL}
                  />
                  <FieldError className="text-xs font-medium">{fieldError("firstName")}</FieldError>
                </Field>

                <Field data-invalid={!!fieldError("lastName") || undefined}>
                  <FieldLabel htmlFor="lastName" className="text-[13px] font-medium">
                    Last name <span className="text-brand">*</span>
                  </FieldLabel>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formik.values.lastName}
                    onChange={(e) => handleNameChange("lastName", e.target.value)}
                    onBlur={(e) => {
                      formik.setFieldTouched("lastName", true, false);
                      handleNameChange("lastName", e.target.value);
                    }}
                    aria-invalid={!!fieldError("lastName")}
                    className={FIELD_SHELL}
                  />
                  <FieldError className="text-xs font-medium">{fieldError("lastName")}</FieldError>
                </Field>
              </div>

              <Field data-invalid={!!fieldError("companyName") || undefined}>
                <FieldLabel htmlFor="companyName" className="text-[13px] font-medium">
                  Company name <span className="text-brand">*</span>
                </FieldLabel>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Acme Inc."
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.setFieldTouched("companyName", true, false);
                    formik.setFieldValue("companyName", e.target.value);
                  }}
                  aria-invalid={!!fieldError("companyName")}
                  className={FIELD_SHELL}
                />
                <FieldError className="text-xs font-medium">{fieldError("companyName")}</FieldError>
              </Field>

              <Field data-invalid={!!fieldError("email") || showExisting || undefined}>
                <FieldLabel htmlFor="email" className="text-[13px] font-medium">
                  Work email <span className="text-brand">*</span>
                </FieldLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    // formik.handleBlur only marks the field touched — it
                    // never resyncs `values.email` from the DOM. Browser
                    // autofill can set the field's value without firing
                    // onChange at all, which would otherwise leave a stale
                    // value (and its error) behind a value that now looks
                    // correct. Blur fires reliably regardless, so resync
                    // from the real DOM value here first.
                    //
                    // setFieldTouched and setFieldValue each schedule their
                    // own validate pass; calling both back to back races
                    // them; skip the first one (`false`) so setFieldValue's
                    // pass — the one with the corrected value — is the only
                    // one that runs.
                    formik.setFieldTouched("email", true, false);
                    formik.setFieldValue("email", e.target.value);
                  }}
                  aria-invalid={!!fieldError("email") || showExisting}
                  className={FIELD_SHELL}
                />
                <FieldError className="text-xs font-medium">
                  {showExisting ? (
                    <>
                      An account with this email already exists.{" "}
                      <Link
                        href="/auth/login"
                        replace
                        className="font-medium text-brand underline-offset-4 hover:underline"
                      >
                        Sign in
                      </Link>{" "}
                      instead.
                    </>
                  ) : fieldError("email") === SERVER_EMAIL_ERROR ? (
                    <>
                      Please use non-disposable Valid Work Email or{" "}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline hover:text-blue-700">
                        Contact Sales
                      </a>{" "}
                      team.
                    </>
                  ) : (
                    fieldError("email")
                  )}
                </FieldError>
              </Field>

              <Button
                type="submit"
                disabled={loading || !isDetailsComplete}
                className="group h-11 w-full rounded-xl bg-brand text-white hover:bg-brand/90"
              >
                {loading ? (
                  <>
                    <Spinner />
                    Sending code…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </FieldGroup>
          </form>
        </>
      ) : (
        <>
          <form onSubmit={submitOtp}>
            <FieldGroup className="mt-6 gap-4">
              <div className="grid grid-cols-6 gap-2">
                {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                  <Input
                    key={i}
                    ref={(el) => {
                      otpRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={otp[i] ?? ""}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                    onKeyDown={(e) => handleOtpKeyDown(e, i)}
                    onPaste={handleOtpPaste}
                    className="h-12 w-full rounded-xl px-0 text-center text-lg font-semibold"
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== OTP_LENGTH}
                className="group h-11 w-full rounded-xl bg-brand text-white hover:bg-brand/90"
              >
                {loading ? (
                  <>
                    <Spinner />
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify & continue
                    <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setStep("details")}
                  className="h-auto p-0 font-medium text-muted-foreground underline-offset-4 hover:underline"
                >
                  Edit details
                </Button>
                <Button
                  type="button"
                  variant="link"
                  onClick={resendOtp}
                  disabled={cooldown > 0 || loading}
                  className="h-auto p-0 font-medium text-muted-foreground underline-offset-4 hover:underline disabled:text-muted-foreground/60 disabled:no-underline"
                >
                  {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </>
      )}

      <p className="mt-5 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        {/* replace, not push — bouncing between register and login while
        deciding which to use shouldn't stack up history entries that
        later surface on repeated back-presses and get bounced forward
        again by proxy.ts once the person is actually signed in. */}
        <Link
          href="/auth/login"
          replace
          className="font-medium text-brand underline-offset-4 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
